import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowRight, Search } from "lucide-react";
import { useAccountProfile } from "@/hooks/use-account-type";
import { exploreGroups, filterGroups, type ExploreItem } from "@/lib/explore-sections";

export const Route = createFileRoute("/_app/explore")({
  head: () => ({
    meta: [
      { title: "Esplora tutte le sezioni — Lumen" },
      {
        name: "description",
        content:
          "Tutti gli strumenti di Lumen: tutor AI, mappe, flashcard, quiz, planner, programmi e community.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: ExplorePage,
});

function ExplorePage() {
  const { t } = useTranslation();
  const account = useAccountProfile();
  const [query, setQuery] = useState("");

  const label = (item: ExploreItem) =>
    `${t(`explore.items.${item.key}.title`)} ${t(`explore.items.${item.key}.sub`)}`;

  const groups = useMemo(
    () => filterGroups(exploreGroups(account.active, account.roles), query, label),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account.active, account.roles, query, t],
  );

  return (
    <div className="space-y-5 px-5 pt-8">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t("explore.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("explore.subtitle")}</p>
      </header>

      <div className="relative">
        <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("explore.search")}
          aria-label={t("explore.search")}
          className="pl-9"
        />
      </div>

      {groups.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t("explore.empty")}</p>
      ) : (
        groups.map((group, gi) => (
          <section
            key={group.key}
            aria-labelledby={`explore-group-${group.key}`}
            className={`section-block ${GROUP_TONE[group.key] ?? TONES[gi % TONES.length]}`}
          >
            <h2 id={`explore-group-${group.key}`} className="section-title mb-2 px-1">
              {t(`explore.groups.${group.key}`)}
            </h2>
            <ul className="grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2">
              {group.items.map((item) => (
                <li key={item.to} className="min-w-0">
                <Link
                  to={item.to as never}
                  className="block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
                >
                  <Card
                    className={`${GROUP_PASTEL[group.key] ?? PASTELS[gi % PASTELS.length]} grid h-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border-0 p-3 transition-shadow hover:shadow-[var(--shadow-soft)] active:scale-[0.99]`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        {t(`explore.items.${item.key}.title`)}
                      </span>
                      <span className="block truncate text-xs text-foreground/60">
                        {t(`explore.items.${item.key}.sub`)}
                      </span>
                    </span>
                    <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground/40" />
                  </Card>
                </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

const TONES = ["tone-sky", "tone-sage", "tone-lilac", "tone-peach"];

const PASTELS = ["pastel-sky", "pastel-sage", "pastel-lilac", "pastel-peach", "pastel-cream"];

const GROUP_PASTEL: Record<string, string> = {
  study: "pastel-sky",
  create: "pastel-lilac",
  review: "pastel-peach",
  plan: "pastel-sage",
  social: "pastel-lilac",
  account: "pastel-sky",
  teach: "pastel-sage",
  family: "pastel-peach",
};

const GROUP_TONE: Record<string, string> = {
  study: "tone-sky",
  create: "tone-lilac",
  review: "tone-peach",
  plan: "tone-sage",
  social: "tone-lilac",
  account: "tone-sky",
  teach: "tone-sage",
  family: "tone-peach",
};

